export type EstimateEmailDisplayState =
  | 'not_sent'
  | 'sending'
  | 'stalled'
  | 'failed'
  | 'sent'

interface EstimateEmailFields {
  estimateEmailStatus?: 'sending' | 'accepted' | 'failed'
  estimateEmailLastAttemptAt?: unknown
  estimateEmailFailureCode?: string
  estimateSentAt?: unknown
}

export interface EstimateEmailDisplay {
  state: EstimateEmailDisplayState
  label: string
  description: string
}

const STALLED_AFTER_MS = 5 * 60 * 1000

export function timestampToMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null

  if (value instanceof Date) {
    const milliseconds = value.getTime()
    return Number.isFinite(milliseconds) ? milliseconds : null
  }

  if (typeof value === 'number') return Number.isFinite(value) ? value : null

  if (typeof value === 'string') {
    const milliseconds = Date.parse(value)
    return Number.isFinite(milliseconds) ? milliseconds : null
  }

  if (typeof value !== 'object') return null

  const timestamp = value as {
    toMillis?: () => number
    toDate?: () => Date
    seconds?: unknown
    _seconds?: unknown
    nanoseconds?: unknown
    _nanoseconds?: unknown
  }

  try {
    if (typeof timestamp.toMillis === 'function') {
      const milliseconds = timestamp.toMillis()
      return Number.isFinite(milliseconds) ? milliseconds : null
    }
    if (typeof timestamp.toDate === 'function') {
      return timestampToMillis(timestamp.toDate())
    }
  } catch {
    return null
  }

  const seconds = timestamp.seconds ?? timestamp._seconds
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return null
  const nanoseconds = timestamp.nanoseconds ?? timestamp._nanoseconds
  const nanos = typeof nanoseconds === 'number' && Number.isFinite(nanoseconds)
    ? nanoseconds
    : 0
  return seconds * 1_000 + nanos / 1_000_000
}

export function estimateEmailFailureMessage(code: string | undefined): string {
  switch (code) {
    case 'provider_rejected':
      return 'The email provider rejected the message. Check the client address and try again.'
    case 'provider_response_invalid':
      return 'The email provider did not return a message ID, so the estimate was not marked as sent.'
    case 'provider_unavailable':
      return 'The email provider could not be reached. Try again, then check the function logs if it continues.'
    default:
      return 'The estimate email was not marked as sent. Review the error and try again.'
  }
}

export function getEstimateEmailDisplay(
  engagement: EstimateEmailFields,
  nowMs = Date.now()
): EstimateEmailDisplay {
  if (engagement.estimateEmailStatus === 'failed') {
    return {
      state: 'failed',
      label: 'Send failed',
      description: estimateEmailFailureMessage(engagement.estimateEmailFailureCode),
    }
  }

  if (engagement.estimateEmailStatus === 'sending') {
    const attemptedAtMs = timestampToMillis(engagement.estimateEmailLastAttemptAt)
    const stalled = attemptedAtMs !== null && nowMs - attemptedAtMs >= STALLED_AFTER_MS
    return stalled
      ? {
          state: 'stalled',
          label: 'Send needs attention',
          description: 'The send started but no result was recorded. Check the function logs, then retry if needed.',
        }
      : {
          state: 'sending',
          label: 'Sending…',
          description: 'The estimate is being submitted to the email provider.',
        }
  }

  if (engagement.estimateEmailStatus === 'accepted' || engagement.estimateSentAt) {
    return {
      state: 'sent',
      label: 'Accepted for delivery',
      description: 'The email provider accepted the estimate for delivery.',
    }
  }

  return {
    state: 'not_sent',
    label: 'Not sent',
    description: 'No successful estimate email is recorded.',
  }
}
